import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { DeviceResponseType } from 'lavva.exalushome/build/js/Services/Devices/IDevice';
import { IDeviceChannel } from 'lavva.exalushome/build/js/Services/Devices/IDeviceChannel';
import { FloodStateHistory } from 'lavva.exalushome/build/js/Services/StatesHistory/StatesHistory';
import { ResponsiveContainer, Scatter, ScatterChart, Tooltip, XAxis, YAxis } from 'recharts';
import { IconLeak } from '../../../../../../assets/control-icons/icon-leak';
import { ControlWrapper, TimeRanges } from '../../../../../../components';
import { RangeHeader } from '../../../../../../components/range-header';
import { useInstallationContext } from '../../../../../../context/installation';
import { useChartData } from '../../../../hooks/charts';
import { ChartEmptyContainer } from '../components/chart-empty-container/chart-empty-container';
import CustomizedXTick from '../components/customized-x-tick';
import { SensorChartTooltip } from '../components/tooltip';
import { useNavigateCharts } from '../hooks/use-history-charts';

type FloodChartProps = {
  channel: IDeviceChannel;
  active?: boolean;
};

const FloodChart: React.FC<FloodChartProps> = ({ channel, active }) => {
  const { t } = useTranslation('channel-details');
  const { selectedInstallation } = useInstallationContext();
  const { activeRange, page, currentRangeLabel, dateFromRange, onClickActiveMeasurementRange, setPage } =
    useNavigateCharts();
  const { data: chartData, isLoading } = useChartData<FloodStateHistory>(
    channel,
    DeviceResponseType.FloodSensorState,
    activeRange,
    dateFromRange,
  );

  const data = useMemo(
    () =>
      chartData?.Data.map((x) => ({
        timestamp: new Date(x.Time).getTime(),
        flood: x.Values?.State ? 1 : 0,
      })),
    [chartData?.Data],
  );

  return (
    <>
      <ControlWrapper className={classNames('control-wrapper--full-space no-center', { active: active })}>
        <div className="exalus-chart-container">
          <TimeRanges
            activeMeasurementRange={activeRange}
            setActiveMeasurementRange={onClickActiveMeasurementRange}
            disableMonth
            disableYear
          />
          <RangeHeader prevAvailable={true} nextAvailable={page < 0} range={currentRangeLabel} setPage={setPage} />
          {data && data.length > 0 ? (
            <div className="binary-chart">
              <ResponsiveContainer width="100%" height={150}>
                <ScatterChart
                  width={500}
                  height={150}
                  margin={{
                    top: 15,
                    right: 20,
                    left: 40,
                    bottom: 0,
                  }}
                >
                  <XAxis
                    dataKey="timestamp"
                    type="number"
                    domain={['dataMin', 'dataMax']}
                    tick={<CustomizedXTick />}
                    height={44}
                    scale="time"
                    allowDuplicatedCategory={!false}
                  />
                  <YAxis dataKey="flood" domain={[0, 1]} ticks={[0, 1]} hide />
                  <Tooltip content={<SensorChartTooltip />} />
                  <Scatter data={data} fill={selectedInstallation?.hexColor} />
                </ScatterChart>
              </ResponsiveContainer>
              <div className="axis-icon">
                <IconLeak isOn={true} />
                <span>{t('exalus.floodDetected.heading')}</span>
              </div>
              <div className="axis-icon">
                <IconLeak isOn={false} />
                <span>{t('exalus.none')}</span>
              </div>
            </div>
          ) : (
            <ChartEmptyContainer isLoading={isLoading} height={150} />
          )}
        </div>
      </ControlWrapper>
    </>
  );
};

export default FloodChart;
